import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { selectUser } from "../../features/userSlice";
import { Card, PageHeader, PageShell, SectionHeader } from "../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../components/UI/States";
import {
  ActivityCalendar,
  BackLink,
  Fact,
  InspectionDialog,
  InspectionTable,
  Notice,
  Pill,
  ResultChip,
  Segmented,
  ServiceChip,
  ServiceDialog,
  ServiceNotice,
} from "../../components/Fleet/FleetParts";
import FleetMap from "../../components/Fleet/FleetMap";
import {
  HISTORY_DAYS,
  downloadText,
  formatAge,
  formatDate,
  formatDateTime,
  formatDuration,
  formatMiles,
  gpsState,
  inspectionCsvColumns,
  isFlagged,
  localDayKey,
  mapsUrl,
  relativeDay,
  toCsv,
  vehicleModel,
} from "../../components/Fleet/fleetFormat";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RestoreFromTrashRoundedIcon from "@mui/icons-material/RestoreFromTrashRounded";
import { ConfirmDialog } from "../../components/Fleet/ForkliftDialogs";
import { EditVehicleDialog, vehicleOrigin } from "../../components/Fleet/VehicleDialogs";
import { fetchVehicle, setVehicleService, updateVehicle } from "./fleetApi";
import { odometerOf } from "./VehicleList";

const REFRESH_MS = 2 * 60 * 1000;

// Inspections come from the mobile app; "No inspection" days are the
// automatic ones from the morning/night GPS odometer readings.
const FILTERS = {
  all: { label: "All", test: () => true },
  inspections: { label: "Inspections", test: (e) => e.source === "mobile" },
  flagged: { label: "Flagged", test: isFlagged },
  auto: { label: "No inspection", test: (e) => e.source === "gps" },
  service: { label: "Service changes", test: (e) => e.source === "status" },
};

const VehicleDetail = () => {
  const { key } = useParams();
  const user = useSelector(selectUser);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");
  const [day, setDay] = useState(null);
  const [dialog, setDialog] = useState(null); // "edit" | "service" | "remove" | "restore"
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      setData(await fetchVehicle(user.id, key));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, key]);

  useEffect(() => {
    setData(null);
    setDay(null);
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const entries = useMemo(() => data?.entries || [], [data]);
  const shown = useMemo(
    () =>
      entries
        .filter((e) => FILTERS[filter].test(e))
        .filter((e) => !day || localDayKey(e.at) === day),
    [entries, filter, day]
  );

  if (error && !data) {
    return (
      <PageShell size="2xl">
        <BackLink to="/fleet/vehicles">Vehicles</BackLink>
        <div className="app-card rounded-2xl border">
          <EmptyState icon={LocalShippingRoundedIcon} title="Couldn't open this vehicle." description={error} />
        </div>
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell size="2xl">
        <BackLink to="/fleet/vehicles">Vehicles</BackLink>
        <LoadingState label="Loading vehicle…" />
      </PageShell>
    );
  }

  const { vehicle, gpsError } = data;
  const { gps, info, stats } = vehicle;
  const state = gpsState(gps);
  const flaggedCount = entries.filter(isFlagged).length;
  const latestInspection = entries.find((e) => e.source === "mobile");
  const exportCsv = () =>
    downloadText(
      `vehicle-${vehicle.label.replace(/[^\w-]/g, "")}-inspections.csv`,
      toCsv(shown, inspectionCsvColumns(shown))
    );
  const model = vehicleModel(info);
  const hasFix = gps && gps.lat !== null && gps.lng !== null;
  const origin = vehicleOrigin(vehicle);
  const editedBy = vehicle.edited
    ? [vehicle.edited.by, vehicle.edited.at ? formatDateTime(vehicle.edited.at) : "", vehicle.edited.source === "mobile" ? "(mobile app)" : ""]
        .filter(Boolean)
        .join(" · ")
    : "";

  const closeDialog = () => {
    setDialog(null);
    setActionError(null);
  };

  // Remove / restore: the truck leaves (or rejoins) every list, count and
  // map, here and in the mobile app. Its page and history stay.
  const setRemoved = async (hidden) => {
    setBusy(true);
    setActionError(null);
    try {
      await updateVehicle(user.id, vehicle.key, { hidden });
      closeDialog();
      await load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell size="2xl">
      <InspectionDialog entry={open} onClose={() => setOpen(null)} userId={user?.id} />
      <ServiceDialog
        open={dialog === "service"}
        assetName={`truck ${vehicle.label}`}
        inService={vehicle.inService}
        onClose={closeDialog}
        onSubmit={async (change) => {
          await setVehicleService(user.id, vehicle.key, change);
          closeDialog();
          await load();
        }}
      />
      <EditVehicleDialog
        vehicle={vehicle}
        branches={data.branches || []}
        open={dialog === "edit"}
        onClose={closeDialog}
        onSaved={() => {
          closeDialog();
          load();
        }}
        userId={user?.id}
      />
      <ConfirmDialog
        open={dialog === "remove"}
        title={`Remove truck ${vehicle.label}?`}
        body="It's taken off every vehicle list, count, map and widget here and in the mobile app, so nobody is asked to inspect it. Bistrack isn't changed, its history is kept, and you can restore it any time from Vehicles → Removed."
        confirmLabel="Remove truck"
        danger
        busy={busy}
        error={actionError}
        onClose={closeDialog}
        onConfirm={() => setRemoved(true)}
      />
      <ConfirmDialog
        open={dialog === "restore"}
        title={`Restore truck ${vehicle.label}?`}
        body="It goes back on the vehicle lists, counts and map here and in the mobile app."
        confirmLabel="Restore truck"
        busy={busy}
        error={actionError}
        onClose={closeDialog}
        onConfirm={() => setRemoved(false)}
      />

      <BackLink to="/fleet/vehicles">Vehicles</BackLink>
      <PageHeader
        icon={LocalShippingRoundedIcon}
        tone="teal"
        eyebrow="Vehicle"
        title={vehicle.label}
        description={[model, vehicle.branch].filter(Boolean).join(" · ") || "No details on file"}
        actions={
          <>
            {vehicle.hidden && (
              <button type="button" className="app-btn app-btn-primary" onClick={() => setDialog("restore")}>
                <RestoreFromTrashRoundedIcon sx={{ fontSize: 18 }} /> Restore
              </button>
            )}
            <button type="button" className="app-btn app-btn-secondary" onClick={() => setDialog("edit")}>
              <EditRoundedIcon sx={{ fontSize: 18 }} /> Edit
            </button>
            <button
              type="button"
              className={`app-btn ${vehicle.inService ? "app-btn-secondary" : "app-btn-primary"}`}
              onClick={() => setDialog("service")}
            >
              <PowerSettingsNewRoundedIcon sx={{ fontSize: 18 }} />
              {vehicle.inService ? "Mark out of service" : "Mark in service"}
            </button>
            <button type="button" className="app-btn app-btn-secondary" onClick={load} disabled={loading}>
              <RefreshRoundedIcon sx={{ fontSize: 18 }} /> {loading ? "Refreshing…" : "Refresh"}
            </button>
            {!vehicle.hidden && (
              <button
                type="button"
                className="app-icon-btn text-rose-600 dark:text-rose-400"
                onClick={() => setDialog("remove")}
                aria-label="Remove truck"
                title="Remove truck"
              >
                <DeleteOutlineRoundedIcon />
              </button>
            )}
          </>
        }
      >
        <div className="mt-3 flex flex-wrap gap-2">
          {vehicle.hidden && <Pill tone="slate">Removed</Pill>}
          <ServiceChip inService={vehicle.inService} service={vehicle.service} />
          <Pill tone={state.tone}>{state.label}</Pill>
          {!vehicle.inBistrack && <Pill tone="slate">{vehicle.vehicleId ? "Not in Bistrack" : "GPS only"}</Pill>}
          {vehicle.renamed && <Pill tone="sky">Renamed · {origin.hint(vehicle.bistrackLabel)}</Pill>}
          {vehicle.moved && (
            <Pill tone="sky">Moved{vehicle.bistrackBranch ? ` · ${origin.hint(vehicle.bistrackBranch)}` : ""}</Pill>
          )}
        </div>
      </PageHeader>

      {error && <Notice tone="rose">{error}</Notice>}
      {vehicle.hidden && (
        <Notice tone="slate">
          This truck was removed{editedBy ? ` (${editedBy})` : ""}. It's left out of every vehicle list, count, map
          and widget here and in the mobile app. Its history below is kept — click Restore to put it back.
        </Notice>
      )}
      {gpsError && <Notice>{gpsError}</Notice>}
      <ServiceNotice service={vehicle.service} />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatTile label="Last inspection" value={relativeDay(stats.lastAt)} hint={stats.lastAt ? `${formatDateTime(stats.lastAt)} · ${stats.lastInspector}` : `None in ${HISTORY_DAYS} days`} />
        <StatTile label="Inspections" value={stats.count} hint={`Last ${HISTORY_DAYS} days`} />
        <StatTile
          label="Flagged"
          value={flaggedCount}
          hint="Needs attention or not safe"
          tone={flaggedCount ? "amber" : "default"}
          active={filter === "flagged"}
          onClick={flaggedCount ? () => setFilter(filter === "flagged" ? "all" : "flagged") : undefined}
        />
        <StatTile
          label="Typical inspection time"
          value={stats.medianSeconds !== null ? formatDuration(stats.medianSeconds) : "—"}
          hint={stats.timedCount ? `${stats.quickCount} of ${stats.timedCount} under 30 seconds` : "Not timed"}
          tone={stats.quickCount ? "amber" : "default"}
        />
        <StatTile label="Odometer" value={formatMiles(odometerOf(vehicle))} hint={gps?.odometer ? "From GPS" : stats.lastOdometer ? "From last inspection" : undefined} />
      </div>

      {(stats.missed > 0 || stats.parkedDays > 0 || stats.outOfServiceDays > 0) && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-3">
          <StatTile
            label="Drove, no inspection"
            value={stats.missed}
            hint="Days the odometer moved with no pre-trip"
            tone={stats.missed ? "rose" : "default"}
            active={filter === "auto"}
            onClick={() => setFilter(filter === "auto" ? "all" : "auto")}
          />
          <StatTile
            label="Not in service"
            value={stats.parkedDays}
            hint="No inspection, odometer didn't move"
          />
          <StatTile
            label="Out of service"
            value={stats.outOfServiceDays}
            hint="Days marked out of service"
            tone={stats.outOfServiceDays ? "violet" : "default"}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          {hasFix && (
            <div className="app-card overflow-hidden rounded-2xl border">
              <FleetMap vehicles={[vehicle]} height="320px" zoom={13} showLinks={false} />
            </div>
          )}

          <Card>
            <SectionHeader title="Activity" description="Click a day to see just that day's inspections." />
            <ActivityCalendar entries={entries} selectedDay={day} onSelectDay={setDay} />
          </Card>

          <div className="app-card overflow-hidden rounded-2xl border">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 sm:px-5">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">Inspection history</h2>
                <p className="app-muted text-sm">
                  Pre-trip inspections from the mobile app, plus days with no inspection judged from the GPS odometer. Last {HISTORY_DAYS} days.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {day && (
                  <button type="button" onClick={() => setDay(null)} className="app-chip">
                    {formatDate(`${day}T12:00:00`)} <CloseRoundedIcon sx={{ fontSize: 14 }} />
                  </button>
                )}
                <Segmented
                  label="Filter inspections"
                  value={filter}
                  onChange={setFilter}
                  options={Object.entries(FILTERS).map(([value, f]) => ({
                    value,
                    label: f.label,
                    count: entries.filter(f.test).length,
                  }))}
                />
                <button
                  type="button"
                  className="app-btn app-btn-sm app-btn-secondary"
                  onClick={exportCsv}
                  disabled={!shown.length}
                  title="Download the rows shown, with every checklist answer"
                >
                  <DownloadRoundedIcon sx={{ fontSize: 16 }} /> Export CSV
                </button>
              </div>
            </div>
            <div className="mt-3 border-t app-divider">
              <InspectionTable
                entries={shown}
                onOpen={setOpen}
                show={{ odometer: true }}
                emptyTitle={
                  entries.length
                    ? "No inspections match this filter."
                    : vehicle.vehicleId
                      ? `No inspections in the last ${HISTORY_DAYS} days.`
                      : "This GPS tracker isn't linked to a Bistrack vehicle, so it has no inspections."
                }
              />
            </div>
          </div>
        </div>

        <div className="min-w-0 space-y-6">
          <Card>
            <SectionHeader title="Right now" description={gps ? "Live from GPS Insight." : undefined} />
            {gps ? (
              <>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <Fact label="Status">{state.label}</Fact>
                  <Fact label="Ignition">{gps.ignition ? gps.ignition.toUpperCase() : ""}</Fact>
                  <Fact label="Driver">{gps.driver || "Unassigned"}</Fact>
                  <Fact label="Heading">{gps.heading}</Fact>
                  <div className="col-span-2">
                    <Fact label="Location">
                      {gps.landmark && <span className="block">{gps.landmark}</span>}
                      {gps.address && <span className="app-muted block text-xs font-normal">{gps.address}</span>}
                      {!gps.landmark && !gps.address && "—"}
                    </Fact>
                  </div>
                  <Fact label="Last report">
                    {gps.fixAt ? formatDateTime(gps.fixAt) : ""}
                    {gps.ageMinutes !== null && (
                      <span className="app-muted block text-xs font-normal">{formatAge(gps.ageMinutes)} ago</span>
                    )}
                  </Fact>
                  <Fact label="Battery">{gps.voltage ? `${gps.voltage} V` : ""}</Fact>
                </dl>
                {hasFix && (
                  <a
                    href={mapsUrl(gps.lat, gps.lng)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-btn app-btn-secondary mt-4 w-full justify-center"
                  >
                    Open in Google Maps <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                  </a>
                )}
              </>
            ) : (
              <p className="app-muted text-sm">
                {gpsError ? "Live GPS is unavailable right now." : "No GPS Insight tracker is matched to this vehicle (by VIN or truck number)."}
              </p>
            )}
          </Card>

          <Card>
            <SectionHeader title="Vehicle" />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Fact label="Truck #">
                {vehicle.label}
                {vehicle.renamed && (
                  <span className="app-muted block text-xs font-normal">{origin.hint(vehicle.bistrackLabel)}</span>
                )}
              </Fact>
              <Fact label="Branch">
                {vehicle.branch}
                {vehicle.moved && (
                  <span className="app-muted block text-xs font-normal">
                    {vehicle.bistrackBranch ? origin.hint(vehicle.bistrackBranch) : "Moved here on the Fleet pages"}
                  </span>
                )}
              </Fact>
              <div className="col-span-2">
                <Fact label="VIN">
                  <span className="font-mono text-xs">{vehicle.vin}</span>
                </Fact>
              </div>
              <Fact label="Year">{info?.year}</Fact>
              <Fact label="Make">{info?.make}</Fact>
              <Fact label="Model">{info?.model}</Fact>
              <Fact label="Fuel">{info?.fuel}</Fact>
              <div className="col-span-2">
                <Fact label="Body">{info?.bodyClass}</Fact>
              </div>
              {info?.gvwr && (
                <div className="col-span-2">
                  <Fact label="GVWR">{info.gvwr}</Fact>
                </div>
              )}
              <Fact label="Bistrack ID">{vehicle.vehicleId}</Fact>
              <Fact label="GPS tracker">{gps?.id}</Fact>
              {editedBy && (
                <div className="col-span-2">
                  <Fact label="Last edited">{editedBy}</Fact>
                </div>
              )}
            </dl>
            {info && (
              <p className="app-muted mt-3 text-[11px]">Year, make and model are decoded from the VIN (NHTSA).</p>
            )}
          </Card>

          {stats.lastAt && (
            <Card>
              <SectionHeader title="Latest inspection" />
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-semibold">{stats.lastInspector || "—"}</p>
                  <p className="app-muted text-xs tabular-nums">{formatDateTime(stats.lastAt)}</p>
                </div>
                <ResultChip result={stats.lastResult} />
              </div>
              {latestInspection && (
                <button type="button" className="app-btn app-btn-secondary mt-3 w-full justify-center" onClick={() => setOpen(latestInspection)}>
                  View checklist
                </button>
              )}
            </Card>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default VehicleDetail;
